/**
 * Anomaly Detail Body
 *
 * Was a popup dialog opened over the Anomalies list; now the content of a
 * real route (/anomalies/:id), matching what the Recommendation detail view
 * already does. Five tabs, a Recharts cost-trend chart and a live RCA
 * pipeline is page-scale content, not dialog-scale - and a route makes it
 * bookmarkable, shareable and safe to refresh, none of which a modal was.
 *
 * This file deliberately keeps the three status actions (Acknowledge /
 * Resolve / False Positive) rather than pushing them up to the page: they
 * depend on this component's own toast, actor and loading state, and moving
 * them would have meant duplicating all of that for no visual gain.
 */

import React, { useState, useEffect } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
} from 'recharts';
import { Info, Search, TrendingUp, History as HistoryIcon, Check, CheckCircle2, X as XIcon } from 'lucide-react';
import { Anomaly, AnomalyCluster, acknowledgeAnomaly, resolveAnomaly, markAnomalyAsFalsePositive, getAnomalyCluster } from '../../services/anomalyService';
import RCAAnalysisTab from '../anomaly/RCAAnalysisTab';
import { useAuthStore } from '../../store/authStore';
import { getActiveTenantId } from '../../services/tenantContext';
import { CHART_COLORS, chartGridClass, chartTickStyle, chartTooltipStyle } from '../../utils/chartTheme';
import { PillTabs } from '../common/Modal';
import { Card } from '../common/Card';
import { Badge, BadgeVariant } from '../common/Badge';
import { Button } from '../common/Button';

interface AnomalyDetailBodyProps {
  anomaly: Anomaly;
  onRefresh?: () => void;
  /** Fired after an action that ends this anomaly's open lifecycle (resolve /
   *  false positive), so the page can navigate back to the list. */
  onActionComplete?: () => void;
}

interface StatusHistory {
  id: number;
  old_status: string | null;
  new_status: string;
  changed_by: string;
  changed_at: string;
  notes: string | null;
  resolution: string | null;
}

interface ParsedResource {
  name: string;
  account?: string;
  region?: string;
  resourceType?: string;
}

/**
 * AC-ANOM-003 requires every anomaly to carry "at least one reason code" that a
 * user can see. The backend has computed these all along
 * (anomaly-quality.service.ts ANOMALY_REASON_CODES) but nothing rendered them,
 * so the only "why" a user ever got was the free-text message. Plain-English
 * gloss for each code, since the raw SCREAMING_SNAKE codes mean nothing to an
 * analyst reading an alert.
 */
const REASON_CODE_LABELS: Record<string, { label: string; detail: string; tone: BadgeVariant }> = {
  STATISTICAL_OUTLIER: { label: 'Statistical outlier', detail: 'Cost fell outside the normal statistical range for this service.', tone: 'warning' },
  TREND_DEVIATION: { label: 'Off trend', detail: 'Cost diverged from the trend projected by recent history.', tone: 'warning' },
  NEW_SERVICE_INTRODUCED: { label: 'New service', detail: 'This service had no recorded spend in the baseline window.', tone: 'info' },
  SUSTAINED_COST_INCREASE: { label: 'Sustained increase', detail: 'The higher cost held over several days rather than for a single day.', tone: 'warning' },
  DAY_OF_WEEK_DEVIATION: { label: 'Unusual for this weekday', detail: 'Cost is high compared with the same day of week historically.', tone: 'warning' },
  MULTI_DETECTOR_AGREEMENT: { label: 'Multiple detectors agree', detail: 'More than one independent detector flagged this — a strong signal.', tone: 'success' },
  ENSEMBLE_VOTE: { label: 'Ensemble confirmed', detail: 'The multi-model ensemble voted this anomalous.', tone: 'success' },
  INSUFFICIENT_BASELINE_HISTORY: { label: 'Thin history', detail: 'Not enough past data for a fully confident baseline, so severity is capped.', tone: 'muted' },
  SINGLE_SIGNAL_ONLY: { label: 'Single signal only', detail: 'Only one detector flagged this, so confidence is capped at 0.3 and it is informational rather than a confirmed alert.', tone: 'muted' },
  LOW_DOLLAR_IMPACT: { label: 'Low dollar impact', detail: 'The absolute cost movement is small.', tone: 'muted' },
  WITHIN_CHANGE_WINDOW: { label: 'During a known change', detail: 'This overlapped a known deployment or change window, which lowers confidence.', tone: 'muted' },
  COHORT_CONSISTENT: { label: 'Normal vs peers', detail: 'Comparable to similar workloads across the anonymised cohort.', tone: 'muted' },
  COHORT_OUTLIER: { label: 'Outlier vs peers', detail: 'High compared with similar workloads across the anonymised cohort.', tone: 'warning' },
};

/**
 * FR-ANOM-007 layer 7 - the spec's own alert threshold. Anything at or below
 * this is, by the spec's definition, not a confirmed alert. Surfacing it is
 * honest; silently styling a 0.3-confidence finding exactly like a 0.9 one is
 * not.
 */
const ALERT_CONFIDENCE_THRESHOLD = 0.7;

/**
 * Recovers the account / region / resource-type that are ALREADY encoded inside
 * a cloud resource identifier.
 *
 * The detector stores the full ARN in `top_resources[].resource_id` (e.g.
 * `arn:aws:ec2:us-east-1:739572512568:dedicated-host/h-09dd11ec7b4a65926`), but
 * the UI only ever rendered the trailing name - so "which AWS account? which
 * region? what kind of resource?" was invisible even though the answer was
 * sitting in the string the whole time.
 *
 * Deliberately never GUESSES: a bare id like `i-064197...` carries no account or
 * region, so those come back undefined and the UI shows them as unknown rather
 * than inventing a plausible-looking value.
 */
function parseResourceId(resourceId: string, provider: string): ParsedResource {
  if (!resourceId) return { name: '' };

  // AWS ARN: arn:partition:service:region:account-id:resource-type/resource-id
  if (resourceId.startsWith('arn:')) {
    const parts = resourceId.split(':');
    const tail = parts.slice(5).join(':');
    const slash = tail.lastIndexOf('/');
    return {
      name: (slash >= 0 ? tail.substring(slash + 1) : tail) || resourceId,
      region: parts[3] || undefined,
      account: parts[4] || undefined,
      resourceType: slash >= 0 ? `${parts[2]}:${tail.substring(0, slash)}` : parts[2] || undefined,
    };
  }

  // Azure: /subscriptions/<sub>/resourceGroups/<rg>/providers/<ns>/<type>/<name>
  if (resourceId.startsWith('/subscriptions/')) {
    const seg = resourceId.split('/').filter(Boolean);
    const subIdx = seg.indexOf('subscriptions');
    const provIdx = seg.indexOf('providers');
    return {
      name: seg[seg.length - 1] || resourceId,
      account: subIdx >= 0 ? seg[subIdx + 1] : undefined,
      resourceType: provIdx >= 0 ? seg.slice(provIdx + 1, seg.length - 1).join('/') : undefined,
    };
  }

  // Bare id - only the type is genuinely inferable from the prefix.
  const bare: ParsedResource = { name: resourceId };
  if (provider === 'aws') {
    const prefixes: Record<string, string> = {
      'i-': 'ec2:instance',
      'vol-': 'ec2:volume',
      'nat-': 'ec2:natgateway',
      'snap-': 'ec2:snapshot',
      'h-': 'ec2:dedicated-host',
      'eni-': 'ec2:network-interface',
    };
    const hit = Object.keys(prefixes).find((p) => resourceId.startsWith(p));
    if (hit) bare.resourceType = prefixes[hit];
  }
  return bare;
}

export const AnomalyDetailBody: React.FC<AnomalyDetailBodyProps> = ({ anomaly, onRefresh, onActionComplete }) => {
  const [activeTab, setActiveTab] = useState<'overview' | 'detection' | 'rca' | 'trend' | 'history'>('overview');
  const [history, setHistory] = useState<StatusHistory[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [showToast, setShowToast] = useState<{ message: string; type: 'success' | 'error' } | null>(null);
  const [enrichedMetadata, setEnrichedMetadata] = useState<any>(anomaly.metadata || {});
  const [cluster, setCluster] = useState<AnomalyCluster | null>(null);
  const [fetchedTrend, setFetchedTrend] = useState<Array<{ date: string; cost: number }> | null>(null);
  const [trendLoading, setTrendLoading] = useState(false);
  const [trendError, setTrendError] = useState<string | null>(null);
  const { user } = useAuthStore();
  const actorEmail = user?.email || 'unknown-user';

  const OPT_API = import.meta.env.VITE_OPTIMIZATION_API_URL
    ? `${import.meta.env.VITE_OPTIMIZATION_API_URL}/api/v1`
    : 'http://localhost:3005/api/v1';

  const COST_API = import.meta.env.VITE_COST_API_URL
    ? `${import.meta.env.VITE_COST_API_URL}/api/v1`
    : 'http://localhost:3001/api/v1';

  // optimization-api now requires a verified Bearer token on every request
  // (previously had no auth at all - a real gap fixed alongside this).
  const authHeaders = (): Record<string, string> => {
    const token = localStorage.getItem('accessToken');
    return {
      'x-tenant-id': getActiveTenantId(),
      'x-user-id': 'user-123',
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    };
  };

  // Fetch enriched anomaly data (with resource info) on mount
  useEffect(() => {
    const fetchEnriched = async () => {
      try {
        const response = await fetch(`${OPT_API}/anomalies/${anomaly.id}`, {
          headers: authHeaders(),
        });
        const data = await response.json();
        if (data.success && data.data?.metadata) {
          setEnrichedMetadata(data.data.metadata);
        }
      } catch (err) {
        // Silently ignore - use original metadata
      }
    };
    fetchEnriched();
  }, [anomaly.id]);

  // FR-ANOM-010 - only fetched when this anomaly was actually grouped with
  // others (cluster_id set); most anomalies have none, so this is a no-op
  // for them rather than an extra request every time.
  useEffect(() => {
    if (!anomaly.cluster_id) {
      setCluster(null);
      return;
    }
    getAnomalyCluster(anomaly.cluster_id)
      .then(setCluster)
      .catch(() => setCluster(null));
  }, [anomaly.cluster_id]);

  // Load history when History tab is selected
  useEffect(() => {
    if (activeTab === 'history') {
      loadHistory();
    }
  }, [activeTab]);

  /**
   * Cost Trend fallback (opened tab only, fetched once).
   *
   * If the anomaly already carries a stored `cost_trend` we use that untouched.
   * Otherwise we ask cost-api for the real daily series for this anomaly's own
   * provider+service around the detection date. This is live billing data, not
   * a reconstruction of what the detector saw, so it's labelled as such in the
   * chart - see the note under the graph.
   */
  useEffect(() => {
    if (activeTab !== 'trend') return;
    if ((enrichedMetadata?.cost_trend?.length ?? 0) > 0) return;
    if (fetchedTrend !== null || trendLoading) return;

    const loadTrend = async () => {
      try {
        setTrendLoading(true);
        setTrendError(null);

        // Window spans the run-up to the anomaly AND everything since, so the
        // analyst can see whether the spike persisted, was corrected, or was a
        // one-day blip. Anchoring the window's end to the detection date (as
        // this first did) hid the single most useful part - what happened next.
        const detected = new Date(anomaly.detected_at);
        const start = new Date(detected);
        start.setDate(start.getDate() - 13);
        const today = new Date();
        const end = today > detected ? today : detected;
        const iso = (d: Date) => d.toISOString().split('T')[0];

        const filter = encodeURIComponent(
          JSON.stringify({ provider: anomaly.provider, service: anomaly.service })
        );
        const url = `${COST_API}/cost/timeseries?start=${iso(start)}&end=${iso(end)}&granularity=daily&filter=${filter}`;

        const response = await fetch(url, { headers: authHeaders() });
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const body = await response.json();

        const series = (body?.data || [])
          .map((row: any) => ({ date: String(row.timestamp).split(' ')[0], cost: Number(row.cost) || 0 }))
          .filter((row: any) => row.date);

        setFetchedTrend(series);
        if (series.length === 0) {
          setTrendError('No daily cost data was returned for this service and date range.');
        }
      } catch (err: any) {
        setFetchedTrend([]);
        setTrendError(err?.message || 'Could not load cost history.');
      } finally {
        setTrendLoading(false);
      }
    };

    loadTrend();
  }, [activeTab, anomaly.id]);

  const loadHistory = async () => {
    try {
      setLoadingHistory(true);
      const response = await fetch(`${OPT_API}/anomalies/${anomaly.id}/history`, {
        headers: authHeaders(),
      });
      const data = await response.json();
      if (data.success) {
        setHistory(data.data);
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const getSeverityVariant = (severity: string): BadgeVariant => {
    switch (severity) {
      case 'critical':
        return 'danger';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'muted';
    }
  };

  const getStatusVariant = (status: string): BadgeVariant => {
    switch (status) {
      case 'open':
        return 'danger';
      case 'acknowledged':
        return 'warning';
      case 'resolved':
        return 'success';
      default:
        return 'muted';
    }
  };

  // Format currency
  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  // Format date/time
  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Toast notification
  const toast = (message: string, type: 'success' | 'error' = 'success') => {
    setShowToast({ message, type });
    setTimeout(() => setShowToast(null), 3000);
  };

  // Action handlers
  const handleAcknowledge = async () => {
    if (actionLoading) return;

    const notes = prompt('Add notes (optional):');
    if (notes === null) return; // User cancelled

    setActionLoading(true);
    try {
      await acknowledgeAnomaly(anomaly.id, actorEmail, notes || undefined);
      toast('Anomaly acknowledged successfully');
      if (onRefresh) {
        onRefresh();
      }
    } catch (error: any) {
      toast('Failed to acknowledge anomaly: ' + error.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleResolve = async () => {
    if (actionLoading) return;

    const resolution = prompt('Enter resolution details:');
    if (!resolution) {
      toast('Resolution details are required', 'error');
      return;
    }

    setActionLoading(true);
    try {
      await resolveAnomaly(anomaly.id, actorEmail, resolution);
      toast('Anomaly resolved successfully');
      if (onRefresh) {
        onRefresh();
      }
      setTimeout(() => onActionComplete?.(), 1500); // Return to the list once the toast has been read
    } catch (error: any) {
      toast('Failed to resolve anomaly: ' + error.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  const handleFalsePositive = async () => {
    if (actionLoading) return;

    const reason = prompt('Why is this a false positive?');
    if (!reason) {
      toast('Reason is required', 'error');
      return;
    }

    setActionLoading(true);
    try {
      await markAnomalyAsFalsePositive(anomaly.id, actorEmail, reason);
      toast('Marked as false positive');
      if (onRefresh) {
        onRefresh();
      }
      setTimeout(() => onActionComplete?.(), 1500); // Return to the list once the toast has been read
    } catch (error: any) {
      toast('Failed to mark as false positive: ' + error.message, 'error');
    } finally {
      setActionLoading(false);
    }
  };

  // Prepare cost trend data for chart.
  // Only the spike and drift detectors persist `cost_trend` into metadata; the
  // new-service detector never has a daily series in hand (it compares two
  // period averages), so those anomalies used to show a dead-end "not
  // available" panel forever. `fetchedTrend` below fills that gap by asking
  // cost-api for the real daily series on demand, which also works for old
  // anomalies detected before any of this existed.
  const storedTrend = enrichedMetadata?.cost_trend || anomaly.metadata?.cost_trend || [];
  const costTrendData = storedTrend.length > 0 ? storedTrend : fetchedTrend || [];
  const trendIsLive = storedTrend.length === 0 && (fetchedTrend?.length ?? 0) > 0;
  const baseline = anomaly.metadata?.baseline;
  const threshold = anomaly.metadata?.threshold;

  // The detector already records the top cost-driving resources (with their
  // full ARNs); the modal previously showed only the trailing name of the first
  // one. Parse each id so account / region / type become visible, and keep the
  // whole ranked list rather than throwing away resources 2..N.
  const topResources: Array<{ resource_id: string; resource_name?: string; cost: number }> =
    enrichedMetadata?.top_resources || anomaly.metadata?.top_resources || [];
  const parsedResources = topResources.map((r) => ({
    ...r,
    parsed: parseResourceId(r.resource_id || r.resource_name || '', anomaly.provider),
  }));
  const primaryParsed = parsedResources[0]?.parsed;
  // Account/region are identical across every resource in the vast majority of
  // anomalies; only claim a single value when they genuinely all agree.
  const uniqueAccounts = Array.from(
    new Set(parsedResources.map((r) => r.parsed.account).filter(Boolean))
  ) as string[];
  const uniqueRegions = Array.from(
    new Set(parsedResources.map((r) => r.parsed.region).filter(Boolean))
  ) as string[];
  // Distinguish "detector scored this 0%" from "no score was ever recorded".
  // `metadata.confidence` is genuinely null on anomalies detected before the
  // quality gate existed - rendering that as a confident "0.0%" was a lie.
  const rawConfidence = enrichedMetadata?.confidence ?? anomaly.metadata?.confidence;
  const hasConfidence = typeof rawConfidence === 'number';
  const confidence = hasConfidence ? rawConfidence : 0;

  // Check which actions are available
  const canAcknowledge = anomaly.status === 'open';
  const canResolve = anomaly.status === 'acknowledged' || anomaly.status === 'open';
  const canMarkFalsePositive = anomaly.status !== 'resolved' && anomaly.status !== 'false_positive';

  const TABS = [
    { key: 'overview', label: 'Overview', icon: Info },
    { key: 'detection', label: 'Detection', icon: Search },
    { key: 'rca', label: 'Root Cause Analysis', icon: Search },
    { key: 'trend', label: 'Cost Trend', icon: TrendingUp },
    { key: 'history', label: 'History', icon: HistoryIcon },
  ];

  return (
    <div>
      {/* Status actions. These sit at the top-right of the body rather than in
          the page header because they own this component's toast and loading
          state; visually they read as part of the header block above them.
          "Raise Ticket" deliberately absent: raising a ticket before any
          investigation has run means filing one with no root cause, no risk
          tier and no SOP attached. The Root Cause Analysis tab's own "Run Full
          Investigation" flow opens a ticket at the point it actually has
          something worth putting in it. */}
      {(canAcknowledge || canResolve || canMarkFalsePositive) && (
        <div className="mb-4 flex flex-wrap items-center justify-end gap-2">
          {/* One primary action, the rest secondary - all on the same `sm`
              scale. These were outline / solid-emerald / outline before, so
              three buttons doing comparable jobs carried three different
              visual weights and the green one read as the loudest thing on
              the page. Resolve is the action you actually want, so it's the
              one that gets emphasis, in the brand colour rather than a hue
              nothing else in the app uses for a button. Fixed min-width so
              the trio aligns instead of stair-stepping with label length. */}
          {canAcknowledge && (
            <Button onClick={handleAcknowledge} disabled={actionLoading} variant="outline" size="sm" className="min-w-[7.5rem]" title="Acknowledge this anomaly">
              <Check /> Acknowledge
            </Button>
          )}
          {canResolve && (
            <Button onClick={handleResolve} disabled={actionLoading} variant="primary" size="sm" className="min-w-[7.5rem]" title="Resolve this anomaly">
              <CheckCircle2 /> Resolve
            </Button>
          )}
          {canMarkFalsePositive && (
            <Button onClick={handleFalsePositive} disabled={actionLoading} variant="outline" size="sm" className="min-w-[7.5rem]" title="Mark as false positive">
              <XIcon /> False positive
            </Button>
          )}
        </div>
      )}

      {showToast && (
        <div
          className={`mb-3 rounded-lg px-3 py-2 text-sm font-medium ${
            showToast.type === 'success'
              ? 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400'
              : 'bg-rose-500/10 text-rose-600 dark:text-rose-400'
          }`}
        >
          {showToast.type === 'success' ? '✓' : '✗'} {showToast.message}
        </div>
      )}

      <PillTabs tabs={TABS} active={activeTab} onChange={(k) => setActiveTab(k as typeof activeTab)} />

      {/* Overview Tab */}
      {activeTab === 'overview' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Service</div>
              <div className="mt-1 text-sm font-medium text-foreground">{anomaly.service}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Provider</div>
              <div className="mt-1 text-sm font-medium text-foreground">{anomaly.provider.toUpperCase()}</div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Severity</div>
              <div className="mt-1"><Badge variant={getSeverityVariant(anomaly.severity)}>{anomaly.severity.toUpperCase()}</Badge></div>
            </Card>
            <Card className="p-3">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Status</div>
              <div className="mt-1"><Badge variant={getStatusVariant(anomaly.status)}>{anomaly.status.toUpperCase()}</Badge></div>
            </Card>
            {uniqueAccounts.length > 0 && (
              <Card className="p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  {anomaly.provider === 'azure' ? 'Subscription' : 'Account'}
                </div>
                <div className="mt-1 truncate font-mono text-sm font-medium text-foreground" title={uniqueAccounts.join(', ')}>
                  {uniqueAccounts.length === 1 ? uniqueAccounts[0] : `${uniqueAccounts.length} accounts`}
                </div>
              </Card>
            )}
            {uniqueRegions.length > 0 && (
              <Card className="p-3">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Region</div>
                <div className="mt-1 truncate text-sm font-medium text-foreground" title={uniqueRegions.join(', ')}>
                  {uniqueRegions.length === 1 ? uniqueRegions[0] : `${uniqueRegions.length} regions`}
                </div>
              </Card>
            )}
            {(primaryParsed?.name || enrichedMetadata?.primary_resource) && (
              <Card className="p-3 col-span-2">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Top Resource</div>
                <div
                  className="mt-1 truncate font-mono text-sm font-medium text-foreground"
                  title={parsedResources[0]?.resource_id || enrichedMetadata?.primary_resource}
                >
                  {primaryParsed?.name || enrichedMetadata?.primary_resource}
                </div>
                {primaryParsed?.resourceType && (
                  <div className="mt-0.5 text-xs text-muted-foreground">{primaryParsed.resourceType}</div>
                )}
              </Card>
            )}
            <Card className="p-3 col-span-2">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">Detected At</div>
              <div className="mt-1 text-sm font-medium text-foreground">{formatDateTime(anomaly.detected_at)}</div>
            </Card>
          </div>

          <Card className="p-4">
            <div className="grid grid-cols-3 gap-4 text-center">
              <div>
                <div className="text-xs text-muted-foreground">Normal Cost</div>
                <div className="mt-1 text-xl font-semibold text-foreground/80">{formatCurrency(anomaly.normal_cost)}</div>
              </div>
              <div className="flex items-center justify-center text-muted-foreground/70 dark:text-muted-foreground">→</div>
              <div>
                <div className="text-xs text-muted-foreground">Current Cost</div>
                <div className="mt-1 text-xl font-semibold text-rose-600 dark:text-rose-400">{formatCurrency(anomaly.current_cost)}</div>
              </div>
            </div>
            <div className="mt-3 text-center">
              <Badge variant="danger">+{anomaly.increase_percent}% increase</Badge>
            </div>
          </Card>

          <Card className="p-4">
            <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-1">Message</div>
            <p className="text-sm text-foreground/80">{anomaly.message}</p>
          </Card>

          {/* The ranked cost drivers behind this anomaly. The detector has
              always recorded these; the modal just never showed anything past
              the first one's short name. */}
          {parsedResources.length > 0 && (
            <Card className="p-4">
              <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground mb-2">
                Top Cost-Driving Resources
              </div>
              <div className="overflow-x-auto">
                <table className="min-w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-wide text-muted-foreground">
                      <th className="py-1.5 pr-3 font-semibold">Resource</th>
                      <th className="py-1.5 pr-3 font-semibold">Type</th>
                      <th className="py-1.5 pr-3 font-semibold">{anomaly.provider === 'azure' ? 'Subscription' : 'Account'}</th>
                      <th className="py-1.5 pr-3 font-semibold">Region</th>
                      <th className="py-1.5 text-right font-semibold">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border dark:divide-border">
                    {parsedResources.map((r, i) => (
                      <tr key={`${r.resource_id}-${i}`}>
                        <td className="py-1.5 pr-3 font-mono text-xs text-foreground" title={r.resource_id}>
                          {r.parsed.name || r.resource_name || r.resource_id}
                        </td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{r.parsed.resourceType || '—'}</td>
                        <td className="py-1.5 pr-3 font-mono text-xs text-muted-foreground">{r.parsed.account || '—'}</td>
                        <td className="py-1.5 pr-3 text-xs text-muted-foreground">{r.parsed.region || '—'}</td>
                        <td className="py-1.5 text-right text-xs font-medium text-foreground">
                          {formatCurrency(r.cost)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {parsedResources.some((r) => !r.parsed.account) && (
                <p className="mt-2 text-xs text-muted-foreground">
                  A dash means the billing record for that resource carried only a bare id, with no account or
                  region attached — shown as unknown rather than guessed.
                </p>
              )}
            </Card>
          )}

          {/* FR-ANOM-010 - only shown when this anomaly was grouped with
              others detected in the same short window. */}
          {cluster && (
            <Card className="p-4 border-amber-200 dark:border-amber-900/40 bg-amber-50/50 dark:bg-amber-900/10">
              <div className="flex items-center justify-between mb-1">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-amber-700 dark:text-amber-400">
                  Part of a cluster — {cluster.member_count} related anomalies
                </div>
                <Badge variant="warning">{cluster.narrative_tier}</Badge>
              </div>
              <p className="text-sm text-foreground/80">{cluster.narrative}</p>
              <details className="mt-2 text-xs text-muted-foreground">
                <summary className="cursor-pointer font-medium">Show all {cluster.member_count} members</summary>
                <ul className="mt-1.5 space-y-1 pl-3">
                  {cluster.members.map((m) => (
                    <li key={m.id}>
                      {m.service} ({m.provider}) — {m.severity}
                      {m.delta_amount != null ? ` · $${m.delta_amount.toFixed(2)}` : ''}
                    </li>
                  ))}
                </ul>
              </details>
            </Card>
          )}
        </div>
      )}

      {/* Detection Tab */}
      {activeTab === 'detection' && (
        <div className="space-y-4">
          <Card className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Detection Method</h3>
            {/* Previously this badge asserted "Statistical Baseline" whenever
                detection_method was absent, directly contradicting the sentence
                below it that said the method wasn't recorded. Say the honest
                thing in both places instead. */}
            <Badge variant={anomaly.metadata?.detection_method ? 'info' : 'muted'}>
              {anomaly.metadata?.detection_method || 'Not recorded'}
            </Badge>
            <p className="mt-2 text-xs text-muted-foreground">
              {anomaly.metadata?.detection_method === 'trend_adjusted_baseline'
                ? "This anomaly was detected by comparing today's cost against a trend-projected expected value (linear growth over the last 30 days), using an adaptive threshold that varies by cost magnitude and service type - not a flat statistical rule."
                : anomaly.metadata?.detection_method === 'statistical_baseline'
                ? "This anomaly was detected using statistical analysis comparing current costs against a 30-day baseline with a 3-sigma threshold (99.7% confidence interval) - used when there wasn't enough recent history to fit a trend."
                : anomaly.metadata?.detection_method === 'period_average_comparison'
                ? `This anomaly was detected by comparing the average daily cost over the recent ${anomaly.metadata?.recent_days ?? 7} days against the average over the preceding ${anomaly.metadata?.historical_days ?? 23} days. Because it compares two period averages rather than scoring each day, it stores no daily chart of its own - the Cost Trend tab loads the real daily series separately.`
                : anomaly.metadata?.detection_method === 'first_appearance'
                ? 'This service had no recorded spend in the preceding baseline window and then began incurring cost, so it was flagged as newly appearing rather than as a change against an existing baseline.'
                : anomaly.metadata?.detection_method === 'linear_regression_drift'
                ? 'This anomaly was detected by fitting a linear trend across the lookback window and flagging a sustained, directional cost drift rather than a single-day spike.'
                : anomaly.metadata?.detection_method === 'ensemble_l1_l2_l3'
                ? 'This anomaly was confirmed by the full three-layer ensemble: a statistical baseline check, a seasonal model, and an isolation-forest outlier model.'
                : 'Detection method not recorded for this anomaly. Anomalies detected from this point on always record one.'}
            </p>
            {(anomaly.metadata?.detector_votes?.length ?? 0) > 0 && (
              <p className="mt-2 text-xs text-muted-foreground">
                <strong>Detector agreement:</strong> {anomaly.metadata!.detector_votes.join(', ')}
                {anomaly.metadata?.multi_signal_confirmed && (
                  <span className="ml-1.5 text-emerald-600 dark:text-emerald-400">✓ confirmed by 2+ independent detectors</span>
                )}
              </p>
            )}
            {/* AC-ANOM-005 - "the active detector tier is visible on every
                alert." Only shown when the backend actually stamped one
                (cold-start/thin-history detections carry it; a mature,
                full-history detection has nothing extra to caveat). */}
            {anomaly.metadata?.detector_tier_note && (
              <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                <strong>Detection maturity:</strong> {anomaly.metadata.detector_tier_note}
              </p>
            )}

            {/* AC-ANOM-003: "at least one reason code" must be visible. These
                were computed by the backend from the start but never rendered,
                so a user's only "why" was the free-text message. */}
            {(anomaly.metadata?.reason_codes?.length ?? 0) > 0 && (
              <div className="mt-3 border-t border-border pt-3 dark:border-border">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Why this fired
                </div>
                <ul className="mt-1.5 space-y-1.5">
                  {anomaly.metadata!.reason_codes.map((code: string) => {
                    const meta = REASON_CODE_LABELS[code];
                    return (
                      <li key={code} className="flex flex-wrap items-baseline gap-2">
                        <Badge variant={meta?.tone || 'muted'}>{meta?.label || code.replace(/_/g, ' ').toLowerCase()}</Badge>
                        <span className="text-xs text-muted-foreground">{meta?.detail || ''}</span>
                      </li>
                    );
                  })}
                </ul>
              </div>
            )}

            {/* AC-ANOM-006: per-model votes are persisted for ensemble-sourced
                anomalies; they were only inspectable via the database until now. */}
            {Array.isArray(anomaly.metadata?.votes) && anomaly.metadata.votes.length > 0 && (
              <div className="mt-3 border-t border-border pt-3 dark:border-border">
                <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  Model votes
                </div>
                <table className="mt-1.5 min-w-full text-xs">
                  <thead>
                    <tr className="text-left text-muted-foreground">
                      <th className="pr-3 font-medium">Model</th>
                      <th className="pr-3 font-medium">Verdict</th>
                      <th className="pr-3 font-medium">Score</th>
                      <th className="font-medium">Confidence</th>
                    </tr>
                  </thead>
                  <tbody>
                    {anomaly.metadata.votes.map((v: any, i: number) => (
                      <tr key={`${v.layer}-${i}`}>
                        <td className="pr-3 py-0.5 text-foreground">{v.layer}</td>
                        <td className="pr-3 py-0.5">
                          <Badge variant={v.is_anomaly ? 'warning' : 'muted'}>{v.is_anomaly ? 'anomaly' : 'normal'}</Badge>
                        </td>
                        <td className="pr-3 py-0.5 text-muted-foreground">{typeof v.score === 'number' ? v.score.toFixed(2) : '—'}</td>
                        <td className="py-0.5 text-muted-foreground">{typeof v.confidence === 'number' ? (v.confidence * 100).toFixed(0) + '%' : '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>

          <Card className="p-4">
            <h3 className="text-sm font-semibold text-foreground mb-2">Confidence Score</h3>
            {hasConfidence ? (
              <>
                <div className="h-2 w-full rounded-full bg-secondary">
                  <div
                    className={`h-2 rounded-full ${confidence > ALERT_CONFIDENCE_THRESHOLD ? 'bg-primary-600' : 'bg-amber-500'}`}
                    style={{ width: `${confidence * 100}%` }}
                  />
                </div>
                <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                  <span>{(confidence * 100).toFixed(1)}%</span>
                  <Badge variant={confidence > ALERT_CONFIDENCE_THRESHOLD ? 'success' : 'warning'}>
                    {confidence > ALERT_CONFIDENCE_THRESHOLD ? 'Confirmed alert' : 'Informational'}
                  </Badge>
                </div>
                {/* FR-ANOM-007 layer 7 sets the alert bar at 0.7. Presenting a
                    0.3-confidence finding with the same visual weight as a
                    0.9 one overstates what the detector actually knows. */}
                {confidence <= ALERT_CONFIDENCE_THRESHOLD && (
                  <p className="mt-2 text-xs text-amber-700 dark:text-amber-400">
                    Below the {ALERT_CONFIDENCE_THRESHOLD * 100}% confidence bar for a confirmed alert, so treat this as
                    a lead to review rather than a verified problem. Confidence is capped when only one detector
                    flags a cost movement, or when there is too little history behind the baseline — the reason codes
                    above say which applies here.
                  </p>
                )}
              </>
            ) : (
              /* A null confidence is NOT 0% - it means this anomaly was detected
                 before the scoring gate existed. Showing "0.0%" made a
                 never-scored anomaly look like a rejected one. */
              <div className="text-xs text-muted-foreground">
                <Badge variant="muted">Not scored</Badge>
                <p className="mt-2">
                  This anomaly was detected before confidence scoring was introduced, so no score was ever
                  recorded. It is not a zero-confidence result. Anomalies detected since then carry a real
                  score here.
                </p>
              </div>
            )}
          </Card>

          {baseline && (
            <Card className="p-4">
              <h3 className="text-sm font-semibold text-foreground mb-3">Baseline Statistics</h3>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                {[
                  ['Mean (Average)', formatCurrency(baseline.mean)],
                  ['Std Deviation', formatCurrency(baseline.std_dev)],
                  ['Min Cost', formatCurrency(baseline.min)],
                  ['Max Cost', formatCurrency(baseline.max)],
                  ['Threshold (3σ)', formatCurrency(baseline.threshold_high)],
                  ['Data Points', `${baseline.data_points || 30} days`],
                ].map(([label, value]) => (
                  <div key={label}>
                    <div className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">{label}</div>
                    <div className="mt-0.5 text-sm font-medium text-foreground">{value}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Root Cause Analysis Tab */}
      {activeTab === 'rca' && (
        <RCAAnalysisTab
          anomaly={anomaly}
          onRefresh={() => {
            // Refresh anomaly list in background without closing modal
            if (onRefresh) {
              onRefresh();
            }
          }}
        />
      )}

      {/* Cost Trend Tab */}
      {activeTab === 'trend' && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">
            {trendIsLive
              ? `Daily Cost — run-up to detection and every day since (${costTrendData.length} days)`
              : `Cost Trend (${costTrendData.length || 7} Days)`}
          </h3>
          {costTrendData.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <LineChart data={costTrendData}>
                <CartesianGrid strokeDasharray="3 3" className={chartGridClass} />
                <XAxis
                  dataKey="date"
                  tickFormatter={(date) => new Date(date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  tick={chartTickStyle}
                />
                <YAxis tickFormatter={(value) => `$${value.toFixed(2)}`} tick={chartTickStyle} />
                <Tooltip
                  contentStyle={chartTooltipStyle}
                  formatter={(value) => (typeof value === 'number' ? [formatCurrency(value), 'Cost'] : ['N/A', 'Cost'])}
                  labelFormatter={(label) => `Date: ${new Date(String(label)).toLocaleDateString()}`}
                />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {baseline && (
                  <>
                    <ReferenceLine y={baseline.mean} stroke={CHART_COLORS.success} strokeDasharray="3 3" label="Baseline" />
                    <ReferenceLine y={threshold || baseline.threshold_high} stroke={CHART_COLORS.danger} strokeDasharray="3 3" label="Threshold" />
                  </>
                )}
                {/* Marks the day this anomaly fired, so the days to the right of
                    it read as "what happened after we flagged it". */}
                {trendIsLive && (
                  <ReferenceLine
                    x={anomaly.detected_at.split('T')[0]}
                    stroke={CHART_COLORS.danger}
                    strokeDasharray="4 2"
                    label={{ value: 'Detected', position: 'top', fontSize: 10, fill: CHART_COLORS.danger }}
                  />
                )}
                <Line type="monotone" dataKey="cost" stroke={CHART_COLORS.primary} strokeWidth={2} name="Daily Cost" />
              </LineChart>
            </ResponsiveContainer>
          ) : trendLoading ? (
            <div className="py-8 text-center text-sm text-muted-foreground">Loading cost history...</div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <p>Cost trend data not available for this anomaly.</p>
              <p className="mt-1 text-xs">{trendError || 'No daily cost history could be loaded for this service.'}</p>
            </div>
          )}
          {trendIsLive && (
            <p className="mt-3 border-t border-border pt-2 text-xs text-muted-foreground dark:border-border dark:text-muted-foreground/70">
              This anomaly was detected by a method that compares period averages rather than a daily
              series, so it stored no chart of its own. The line above is the <strong>current</strong> daily
              billed cost for {anomaly.service} ({anomaly.provider.toUpperCase()}), fetched live just now.
              Cloud providers restate recent billing data for several days, so these figures can differ from
              the headline numbers captured at detection time.
            </p>
          )}
        </Card>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <Card className="p-4">
          <h3 className="text-sm font-semibold text-foreground mb-3">Status History</h3>
          {loadingHistory ? (
            <div className="text-sm text-muted-foreground">Loading history...</div>
          ) : history.length > 0 ? (
            <div className="space-y-3">
              {history.map((item) => (
                <div key={item.id} className="border-l-2 border-primary-300 pl-3 dark:border-primary-700">
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <span className="text-sm">
                      {item.old_status ? (
                        <>
                          <span className="text-muted-foreground">{item.old_status}</span>
                          {' → '}
                          <span className="font-medium text-foreground">{item.new_status}</span>
                        </>
                      ) : (
                        <span className="font-medium text-foreground">{item.new_status}</span>
                      )}
                    </span>
                    <span className="text-xs text-muted-foreground/70">{formatDateTime(item.changed_at)}</span>
                  </div>
                  <div className="mt-0.5 text-xs text-muted-foreground">
                    Changed by: <strong>{item.changed_by}</strong>
                  </div>
                  {item.notes && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      <strong>Notes:</strong> {item.notes}
                    </div>
                  )}
                  {item.resolution && (
                    <div className="mt-0.5 text-xs text-muted-foreground">
                      <strong>Resolution:</strong> {item.resolution}
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="py-8 text-center text-sm text-muted-foreground">
              <p>No status changes yet.</p>
              <p className="mt-1 text-xs">Status changes will appear here when you acknowledge, resolve, or mark this anomaly.</p>
            </div>
          )}
        </Card>
      )}
    </div>
  );
};

export default AnomalyDetailBody;
